import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Brain, ChevronDown } from "lucide-react";
import { cn } from "../../lib/cn";
import { Markdown } from "../Markdown";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../ui/collapsible";

/**
 * Reasoning - a collapsible "thinking / chain-of-thought" disclosure. Renderer
 * for the K2 `reasoning` block (`docs/contracts/block-registry.md`): the
 * platform's chat `BlockRenderer` passes a `reasoning` block's `props` straight
 * through, so the prop names match the block payload exactly.
 *
 * Block props (`{ title?, steps?, text?, defaultOpen? }`):
 *   - `title?` - the trigger label. Defaults to "Thinking".
 *   - `steps?` - discrete reasoning steps, each `{ label, detail? }`. When
 *     present, rendered as an ordered list.
 *   - `text?` - free-form chain-of-thought, rendered as markdown. Used when
 *     there are no discrete `steps`.
 *   - `defaultOpen?` - whether the disclosure starts expanded. Defaults to
 *     `false` (the settled state; a streaming UI opens it live, then lets it
 *     collapse once the answer arrives).
 *
 * Client-only props (never part of the streamed block): `className`, `open` /
 * `onOpenChange` (drive it as a controlled disclosure, e.g. auto-open while a
 * token stream is active and auto-close on completion).
 *
 * Built on the stock `Collapsible` primitive; the panel uses Radix
 * `data-state` open/close animations, disabled under reduced motion. Sources:
 * AI Elements Reasoning + assistant-ui ChainOfThought + prompt-kit Reasoning.
 * Token-themed (`bg-muted/30`, `text-muted-foreground`, ...) with no hardcoded
 * colors. SSR-safe.
 */

export interface ReasoningStep {
  /** The step's headline. */
  label: string;
  /** Optional detail for the step (rendered as markdown). */
  detail?: string;
}

const reasoningVariants = cva(
  // Muted-fill disclosure variant of the shared surface: same radius + hairline
  // as BLOCK_SURFACE, but a `bg-muted/30` fill marks it as secondary chrome.
  cn("not-prose overflow-hidden rounded-2xl border border-border/70 bg-muted/30"),
);

export interface ReasoningProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "title" | "onChange">,
    VariantProps<typeof reasoningVariants> {
  /** Trigger label. Defaults to "Thinking". */
  title?: string;
  /** Discrete reasoning steps. Rendered as an ordered list when present. */
  steps?: ReasoningStep[];
  /** Free-form chain-of-thought, rendered as markdown (used without `steps`). */
  text?: string;
  /** Whether the disclosure starts expanded (uncontrolled). Default `false`. */
  defaultOpen?: boolean;
  /** Controlled open state. Pair with `onOpenChange`. */
  open?: boolean;
  /** Controlled open-state change handler. */
  onOpenChange?: (open: boolean) => void;
}

export const Reasoning = React.forwardRef<HTMLDivElement, ReasoningProps>(function Reasoning(
  { className, title = "Thinking", steps, text, defaultOpen = false, open, onOpenChange, ...rest },
  ref,
) {
  const hasSteps = Array.isArray(steps) && steps.length > 0;
  const hasText = typeof text === "string" && text.trim().length > 0;

  return (
    <Collapsible
      open={open}
      defaultOpen={open === undefined ? defaultOpen : undefined}
      onOpenChange={onOpenChange}
    >
      <div ref={ref} className={cn(reasoningVariants(), className)} data-slot="reasoning" {...rest}>
        <CollapsibleTrigger
          className={cn(
            "group/reasoning flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium text-muted-foreground",
            "transition-colors hover:text-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
          )}
        >
          <Brain className="size-4 shrink-0" aria-hidden />
          <span className="flex-1">{title}</span>
          <ChevronDown
            className="size-4 shrink-0 transition-transform duration-200 group-data-[state=open]/reasoning:rotate-180 motion-reduce:transition-none"
            aria-hidden
          />
        </CollapsibleTrigger>
        <CollapsibleContent
          className={cn(
            "overflow-hidden",
            "data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up",
            "motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none",
          )}
        >
          <div className="border-t border-border/70 px-4 py-3.5 text-sm text-muted-foreground">
            {hasSteps ? (
              <ol className="flex flex-col gap-2.5">
                {steps!.map((step, i) => (
                  <li key={`${step.label}-${i}`} className="flex gap-2.5">
                    <span
                      className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-medium tabular-nums text-muted-foreground"
                      aria-hidden
                    >
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-foreground">{step.label}</p>
                      {step.detail ? (
                        <div className="mt-0.5 [&_p:last-child]:mb-0">
                          <Markdown content={step.detail} />
                        </div>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            ) : hasText ? (
              <div className="[&_p:last-child]:mb-0">
                <Markdown content={text!} />
              </div>
            ) : (
              <p className="italic text-muted-foreground/70">No reasoning to show.</p>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
});

Reasoning.displayName = "Reasoning";

export { reasoningVariants };
export type ReasoningVariantProps = VariantProps<typeof reasoningVariants>;
